import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { getQueries, parseQueries, setQueries, toOptionalBoolean } from '@shared/utils/common';

export type PageViewState<T extends Item<unknown> = Item<unknown>> = T;

export const usePageState = <TState extends PageViewState>(initialState: TState | (() => TState)) => {
  const uuidRef = useRef<string>(uuidv4());

  const [state, setState] = useState<TState>(initialState);

  return useMemo(
    () => ({
      pageState: state,
      uuid: uuidRef.current,

      setPageState: (pageState: TState) => {
        // console.log(uuidRef.current, 'usePageState. setPageState', pageState);
        setState(pageState);
      },

      updatePageState: (pageState: Partial<TState>) => {
        // console.log(uuidRef.current, 'usePageState. updatePageState', pageState);
        setState((prev) => ({ ...prev, ...pageState }));
      },
    }),
    [state]
  );
};

export type UrlViewState<T extends Item<string | boolean> = Item<string | boolean>> = T;

export const useUrlState = <TState extends UrlViewState>(initialState: TState | (() => TState)) => {
  const history = useHistory();
  const mountedRef = useRef(false);
  const stateRef = useRef<TState>({} as TState);
  const uuidRef = useRef<string>(uuidv4());

  const [state, setState] = useState<TState>(initialState);

  const handleSetQueries = () => {
    // Update query keys that exist in our state
    const newQueries = { ...getQueries() };
    for (const [key, urlValue] of Object.entries(stateRef.current)) {
      if (typeof urlValue === 'boolean') {
        newQueries[key] = urlValue ? 'true' : '';
      } else if (typeof urlValue === 'string') {
        newQueries[key] = urlValue;
      }
    }

    // console.log(uuidRef.current, 'useUrlState. handleSetQueries. state: ', { ...stateRef.current });
    // console.log(uuidRef.current, 'useUrlState. handleSetQueries. changed: ', !isEqual(newQueries, getQueries()));

    // We only want to update the URL state if the query params have changed, otherwise we will end up with duplicate entries in the history, messing up the back button
    if (!isEqual(newQueries, getQueries())) {
      if (!mountedRef.current) {
        // console.log(uuidRef.current, 'useUrlState. handleSetQueries. not mounted');
        return;
      }

      // console.log(uuidRef.current, 'useUrlState. handleSetQueries. set queries: ', { ...newQueries });
      setQueries(newQueries);
    }
  };

  useEffect(() => {
    // console.log(uuidRef.current, 'useUrlState. mounting');
    mountedRef.current = true;
    stateRef.current = state;
    handleSetQueries();

    return () => {
      // console.log(uuidRef.current, 'useUrlState. unmounting');
      mountedRef.current = false;

      const currentQueries = { ...getQueries() };
      const newQueries = { ...currentQueries };

      // Remove query keys that exist in our state
      for (const key of Object.keys(stateRef.current)) {
        if (key in newQueries) {
          // console.log(uuidRef.current, 'useUrlState. deleting query: ', key);
          delete newQueries[key];
        }
      }

      // We only want to update the URL state if the query params have changed, otherwise we will end up with duplicate entries in the history, messing up the back button
      if (!isEqual(newQueries, currentQueries)) {
        // console.log(uuidRef.current, 'useUrlState. clearing up queries', Object.keys(stateRef.current));
        setQueries(newQueries);
      }
    };
  }, []);

  // When the queries update, make sure to update state
  useEffect(() => {
    const unregister = history.listen((location) => {
      // console.log(uuidRef.current, 'useUrlState. history.listen. search: ', location.search);

      const currentQueries = { ...parseQueries(location.search) };
      // console.log(uuidRef.current, 'useUrlState. history.listen. current: ', { ...currentQueries });
      const newState = { ...stateRef.current };

      // Pull query key values that exist in our state
      for (const key of Object.keys(newState)) {
        if (typeof newState[key] === 'string') {
          // @ts-ignore
          newState[key] = key in currentQueries ? currentQueries[key] : '';
        } else if (typeof newState[key] === 'boolean') {
          // @ts-ignore
          newState[key] = key in currentQueries ? toOptionalBoolean(currentQueries[key]) ?? false : false;
        }
      }

      if (!mountedRef.current) {
        // console.log(uuidRef.current, 'useUrlState. history.listen. not mounted');
        return;
      }

      // console.log(uuidRef.current, 'useUrlState. history.listen. set state: ', { ...newState });
      stateRef.current = newState;
      setState(newState);
    });

    return () => unregister();
  }, [history]);

  return useMemo(
    () => ({
      urlState: state,
      uuid: uuidRef.current,

      setUrlState: (newState: TState) => {
        // console.log(uuidRef.current, 'useUrlState. setUrlState. state: ', { ...newState });

        if (!isEqual(newState, stateRef.current)) {
          // Set state immediately
          stateRef.current = newState;

          handleSetQueries();
        }
      },

      updateUrlState: (update: Partial<TState>) => {
        // console.log(uuidRef.current, 'useUrlState. updateUrlState. state: ', { ...update });
        const newState = { ...stateRef.current, ...update };

        if (!isEqual(newState, stateRef.current)) {
          // Set state immediately
          stateRef.current = newState;

          handleSetQueries();
        }
      },
    }),
    [state]
  );
};
